import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, LogOut, ChevronDown, LayoutDashboard } from 'lucide-react';
import { getPortalToken, clearPortalToken, getMe } from '../api/portal';
import { fetchProfiles } from '../api/profiles';
import Avatar from './Avatar';
import './BackBar.css';

/**
 * Replaces the full marketing Header (contact bar + nav) on every public jobs page. Owner decision
 * (2026-07-17): applicants don't need the corporate site nav mid-task — just a way back. Two variants:
 *   - default: wordmark (links home) + the account control — used on every page that isn't
 *     literally inside the apply flow: the jobs list, job detail, and 404 (verified 2026-07-23 —
 *     JobDetailPage.js renders plain <BackBar/> with no variant prop, not "task" as this comment
 *     used to claim; that was a stale assumption, not a real per-page choice, and matches the
 *     original 2026-07-17 UX spec, which only calls out the "task" bar for the apply wizard). The
 *     "back to adelgroups.com" link this variant used to also show was removed (owner ask,
 *     2026-07-25) — the wordmark itself already links home.
 *   - task: a single back arrow + short label — used ONLY on the apply wizard (ApplyPage.js), where
 *     the fewer distractions the better (CLAUDE.md S12 UI conventions kept minimal here on purpose).
 *
 * Login/account control (2026-07-18): default variant only, deliberately — the task variant stays
 * untouched (prior owner decision to keep mid-apply screens distraction-free). This is a read-once-
 * at-render token check, same as ApplyPage.js's own one-shot auth check, not a live subscription —
 * a login/logout elsewhere won't update an already-rendered BackBar until the next navigation.
 *
 * Account menu (2026-07-19, owner report): "My account" previously linked to `/` with no way to
 * sign out, so this got a minimal dropdown (signed-in state + Sign out). The candidate dashboard
 * (2026-07-18) now exists at /dashboard — linked from this dropdown above Sign out.
 *
 * First-name greeting (2026-07-19 owner ask): the trigger button and dropdown showed generic "My
 * account" / "Signed in" text. `getMe()` is fetched once on mount (only when a token is present) and
 * the first name is shown once loaded; both spots fall back to the generic text while loading so
 * there's no layout flash — this is the one place in the file that isn't a synchronous token check.
 */
export default function BackBar({ variant = 'default', backTo = '/', backLabel = 'Back', showJobsLink = false }) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  // { profileId, avatarPath, name } | null — feeds the Avatar component's photo-vs-initials decision.
  // `name` prefers the PROFILE's own firstName/name over the account's (owner report, 2026-07-25):
  // "My Profile" edits (PUT /profiles/:id) only ever write CandidateProfile, never CandidateAccount,
  // so an account that self-vivified with no name (e.g. via the login OTP path) can still have a real
  // name recorded on its one profile even though CandidateAccount.firstName stays empty.
  const [avatarInfo, setAvatarInfo] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (variant === 'task' || !getPortalToken()) return;
    let cancelled = false;
    Promise.all([getMe(), fetchProfiles().catch(() => [])]).then(([acc, profiles]) => {
      if (cancelled) return;
      setFirstName(acc.firstName || '');
      const p = profiles[0];
      setAvatarInfo(p ? { profileId: p.id, avatarPath: p.avatarPath, name: p.firstName || p.name || acc.firstName || '' } : null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [variant]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    function onOutsideClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', onOutsideClick);
    return () => document.removeEventListener('mousedown', onOutsideClick);
  }, [menuOpen]);

  function handleSignOut() {
    clearPortalToken();
    setMenuOpen(false);
    navigate('/');
  }

  if (variant === 'task') {
    return (
      <div className="backbar backbar-task">
        <Link to={backTo} className="backbar-arrow" aria-label={backLabel}>
          <ArrowLeft size={18} />
          <span>{backLabel}</span>
        </Link>
      </div>
    );
  }
  const loggedIn = !!getPortalToken();
  return (
    <div className="backbar">
      <div className="backbar-left">
        {showJobsLink && (
          <Link to="/" className="backbar-arrow backbar-jobs-link" aria-label="Back to jobs">
            <ArrowLeft size={16} />
            <span>All jobs</span>
          </Link>
        )}
        <Link to="/" className="backbar-word">
          ADEL<span> GROUP</span>
        </Link>
      </div>
      <div className="backbar-right">
        {/* Persistent apply / "send your CV" entry — the top-right header CTA (owner ask 2026-07-26).
            Lives here rather than as a duplicate bar inside the hero, since this BackBar already IS the
            site header (home-linking wordmark on the left). */}
        {!loggedIn && <Link to="/apply" className="backbar-cta">Send your CV</Link>}
        {loggedIn ? (
          <div className="backbar-menu" ref={menuRef}>
            <button type="button" className="backbar-account backbar-account-btn" onClick={() => setMenuOpen((v) => !v)} aria-label={firstName ? `${firstName}'s account menu` : 'Account menu'}>
              <Avatar profileId={avatarInfo?.profileId} avatarPath={avatarInfo?.avatarPath} name={avatarInfo?.name || firstName} size={22} />
              <ChevronDown size={14} />
            </button>
            {menuOpen && (
              <div className="backbar-dropdown" role="menu">
                <div className="backbar-dropdown-label">{firstName ? `Hi, ${firstName}` : 'Signed in'}</div>
                <Link to="/dashboard" className="backbar-dropdown-item" role="menuitem" onClick={() => setMenuOpen(false)}>
                  <LayoutDashboard size={14} /> Dashboard
                </Link>
                <button type="button" className="backbar-dropdown-item" onClick={handleSignOut}>
                  <LogOut size={14} /> Sign out
                </button>
              </div>
            )}
          </div>
        ) : (
          <Link to="/login" className="backbar-account">
            <User size={14} />
            <span>Login / Register</span>
          </Link>
        )}
      </div>
    </div>
  );
}
